/**
 * Унифицированный выпадающий список (select) с опциями и опциональной подписью.
 */
import type { ComponentPropsWithoutRef } from "react";
import { Text } from "./Text";

export interface SelectOption<T extends string | number = string> {
	value: T;
	label: string;
	disabled?: boolean;
}

type SelectProps<T extends string | number = string> = Omit<
	ComponentPropsWithoutRef<"select">,
	"value" | "onChange" | "children"
> & {
	label?: string;
	bare?: boolean;
	value: T;
	options: readonly SelectOption<T>[];
	onChange: (value: T) => void;
};

export const Select = <T extends string | number = string>({
	label,
	bare = false,
	className = "",
	value,
	options,
	onChange,
	...props
}: SelectProps<T>) => {
	const select = (
		<select
			className={
				bare
					? className
					: `mt-1.5 h-11.5 w-full rounded-lg border border-border bg-surface px-3 text-base text-ink outline-none focus:border-accent ${className}`
			}
			value={value}
			onChange={(event) => {
				const raw = event.target.value;
				const matched = options.find((opt) => String(opt.value) === raw);
				onChange(matched ? matched.value : (raw as T));
			}}
			{...props}
		>
			{options.map((option) => (
				<option key={String(option.value)} value={option.value} disabled={option.disabled}>
					{option.label}
				</option>
			))}
		</select>
	);

	if (bare) return select;
	return (
		<label className="block text-sm font-medium">
			{label && <Text as="span">{label}</Text>}
			{select}
		</label>
	);
};
